import { formatPrice } from 'helpers';
import isString from 'lodash/isString';
import forIn from 'lodash/forIn';
import replace from 'lodash/replace';
import { ICalculation } from 'models/calculationModel';
import { ICustomer } from 'models/models';
import { IPriceResponse } from 'models/priceModel';

const getCustomerColumns = (prop: string, customers: ICustomer[], size = 4) => {
  const cols = [];
  customers.forEach((customer) => {
    if (prop === 'name') {
      cols.push(`${customer.firstName} ${customer.lastName}`);
    } else {
      cols.push(customer[prop] || '');
    }
  });
  for (let i = 0; i < size - customers.length; i += 1) {
    cols.push('');
  }
  return cols;
};

export const getCustomerRow = (title: string, prop: string, customers: ICustomer[], gdpr = false) => {
  const cols = [{ text: title }, ''];
  if (!gdpr) {
    const dataCols = getCustomerColumns(prop, customers);
    cols.push(...dataCols);
  } else {
    const empty = ['[...]', '', '', ''];
    cols.push(...empty);
  }

  return cols;
};

export const getContractorRow = (title: string, price = 0, garagePrice = 0) => [
  title,
  `${formatPrice(Math.round(price * 1.25))} kr`,
  `${formatPrice(Math.round(garagePrice * 1.25))} kr`,
];

const getGeneralContractorRow = (generalContractor: boolean, generalContractorPrice: number) => {
  if (generalContractor) {
    return ['Generalentreprenadsarvode', `${formatPrice(Math.round(generalContractorPrice * 1.25))} kr`, '-'];
  }
  return ['', '', ''];
};

export const getContractorBody = (price: IPriceResponse, calculation: ICalculation) => {
  const {
    foundation,
    construction,
    plumbing,
    electricity,
    painting,
    exteriorPainting,
    tiling,
    totalGaragePrice,
    totalHousePrice,
  } = price?.currentContractorPrices || {};
  const { generalContractor, generalContractorPrice } =
    calculation?.currentHouse.house.contractorCalculation || {};

  const contractorHeading = [{ text: '' }, 'Huvudbyggnad', 'Sidobyggnad'];
  let body = [];

  body = [
    contractorHeading,
    getContractorRow('Mark, grund och yttre VA', foundation?.housePrice, foundation?.garagePrice),
    getContractorRow('Byggåtagande', construction?.housePrice, construction?.garagePrice),
    getContractorRow('Värme- och sanitetsentreprenad', plumbing?.housePrice, plumbing?.garagePrice),
    getContractorRow('Elentreprenad', electricity?.housePrice, electricity?.garagePrice),
    getContractorRow('Invändig målningsentreprenad', painting?.housePrice, painting?.garagePrice),
    getContractorRow(
      'Utvändig målningsentreprenad',
      exteriorPainting?.housePrice,
      exteriorPainting?.garagePrice,
    ),
    getContractorRow('Kakel- och klinkerentreprenad', tiling?.housePrice, tiling?.garagePrice),
    getGeneralContractorRow(generalContractor, generalContractorPrice),
    getContractorRow('Delsummor entreprenader inkl. moms', totalHousePrice, totalGaragePrice),
  ];

  return body;
};

export const getTotalPriceIncVat = (price: IPriceResponse) => {
  const contractorTotalWithVat = price?.currentContractorPrices.totalPrice * 1.25;
  return Math.round(contractorTotalWithVat + price?.currentPriceForHouseAndGarage);
};

export const logo =
  'data:image/png;base64,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';

// Delivery declaration

export const _t = (input: string) => {
  let cleanInput = input;
  const characterMap = [{ å: 'å' }, { ä: 'ä' }, { ö: 'ö' }, { Å: 'Å' }, { Ä: 'Ä' }, { Ö: 'Ö' }];
  characterMap.forEach((char) => {
    forIn(char, (value, key) => {
      cleanInput = replace(cleanInput, new RegExp(value as any, 'g'), key);
    });
  });
  return cleanInput;
};

export const createHeader = (text: any) => {
  return {
    text: isString(text) ? _t(text) : text,
    style: ['infoPages_sectionTitle'],
  };
};
export const createSubHeader = (text: any) => {
  return {
    text: isString(text) ? _t(text) : text,
    style: ['infoPages_sectionSubTitle'],
  };
};
export const createParagraph = (text: any) => {
  return {
    text: isString(text) ? _t(text) : text,
    style: ['infoPages_paragraph'],
  };
};
export const createTableList = (items: any) => {
  const table: any = {
    style: 'infoTable',
    table: {
      widths: ['auto', '*'],
    },
    margin: [20, 10, 100, 0],
    layout: 'noBorders',
  };
  table.table.body = items.map((item: any) => {
    return ['-', _t(item)];
  });
  return table;
};

export const renderGarbo = (deliveryDeclarationSource: any) => {
  if (!deliveryDeclarationSource.renderGarbo) {
    return null;
  }
  return [
    createHeader('SMÅHUSGARANTI'),
    createParagraph(
      'Bra Hus tecknar avtal om en färdigställande försäkring på BRA HUS byggsats. Avtalet omfattar även slutbesiktning av Gar-Bo utsedd besiktningsman.',
    ),
    createParagraph([
      _t('Mer information finns att läsa på nedanstående webbsidor.\n'),
      { text: 'www.tmf.se\n', link: 'http://www.tmf.se', decoration: 'underline' },
      { text: 'www.gar-bo.se', link: 'http://www.gar-bo.se', decoration: 'underline' },
    ]),
  ];
};

export const getConstructionDrawingsTable = (drawings: any) => {
  const table = createTableList(
    drawings.map((drawing: any) => {
      return drawing.name;
    }),
  );
  return table;
};

export const signingLines = (customers: ICustomer[]) => {
  const getTexts = () => {
    const texts = [
      {
        width: 260,
        text: [{ text: '\n' }, { text: 'Ort och datum för Köparens underskrift\n' }],
      },
      {
        width: 260,
        text: [{ text: '\n' }, { text: 'Köparens underskrift' }],
      },
    ];
    return texts;
  };
  const getDots = () => {
    const dots = [
      // Ort & datum, köpare
      {
        type: 'line',
        x1: 0,
        y1: -12,
        x2: 230,
        y2: -12,
        dash: { length: 2 },
        lineWidth: 1,
      },
      {
        type: 'line',
        x1: 260,
        y1: -12,
        x2: 480,
        y2: -12,
        dash: { length: 2 },
        lineWidth: 1,
      },
    ];
    return dots;
  };
  const lines = customers.map(() => [
    {
      margin: [0, 15, 0, 0],
      columns: getTexts(),
    },
    {
      // Streck och linjer
      canvas: getDots(),
    },
  ]);
  return lines;
};

export const formDataTableCell = (microText: string, dataText = '', colSpan = 0) => {
  const cell = {
    text: [
      {
        text: `${microText}\n`,
        style: 'tableMicroText',
      },
      {
        text: dataText,
        style: { bold: true },
      },
    ],
    colSpan,
    border: [true, false, true, true],
  };
  return cell;
};

export const makeCustomersRows = (customers: ICustomer[], gdpr: boolean) => {
  // const rows: any = [];

  if (gdpr) {
    return customers.map((_customer, i) => {
      return [
        formDataTableCell(`Köpare ${i + 1}`, '[...]', 3),
        {},
        {},
        formDataTableCell('Personnr./Org. nr.', '[...]', 2),
        {},
        formDataTableCell('Tel. mobil', '[...]', 2),
        {},
      ];
    });
  }
  customers.map((customer, i) => {
    return [
      formDataTableCell(`Köpare ${i + 1}`, `${customer.firstName} ${customer.lastName}`, 3),
      {},
      {},
      formDataTableCell('Personnr./Org. nr.', customer.personalIdentityNumber, 2),
      {},
      formDataTableCell('Tel. mobil', customer.phoneCell, 2),
      {},
    ];
  });

  return [];
};
